import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as joint from '@joint/plus';
import '@joint/plus/joint-plus.css';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { Grid, Box, Paper, ThemeProvider, StyledEngineProvider, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useIsDarkTheme } from '../../state/hooks/ApplicationHooks';
import { getTheme } from '../../theme';
import { Toolbar } from './components';
import ErrorListDialog from './components/ErrorListDialog';
import TabDataDialog from './components/TabDataDialog.js';
import { inspectorDefinitions } from './config/inspector';
import { sampleGraphs } from './config/sample-graphs';
import './css/material-jointjs-overrides.scss';
import './css/styles.scss';
import { HaloService } from './services/halo-service';
import { InspectorService } from './services/inspector';
import { KeyboardService } from './services/keyboard-service';
import RappidService from './services/kitchensink';
import { NavigatorService } from './services/navigator';
import { StencilService } from './services/stencil-service';
import {
  exportGraphJsonFile,
  importGraphJsonFile,
  updateCellOpacity,
  deleteModuleElements,
} from './utils/import-export.js';
import { getModulesFromGraph, sanitizeModuleName } from './utils/modules';
import convertDateTimetoTime from './utils/timeConverter.js';

const dataKeyMap = [
  { key: 'module', label: 'Module' },
  { key: 'stockChoiceTable', label: 'Choix des stocks' },
  { key: 'outages', label: 'Pannes' },
];

const cleanJson = (obj) => {
  if (Array.isArray(obj)) {
    return obj.filter((item) => item != null).map((item) => cleanJson(item));
  }
  if (obj && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value != null) {
        acc[key] = cleanJson(value);
      }
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Syncs outages from tables.outages to each cell.
 * This ensures cell.get('outages') returns the correct value after graph loading.
 * JointJS fromJSON may not preserve all custom attributes, so we need this sync step.
 */
const syncCellOutagesFromTables = (graph) => {
  const tables = graph.get('tables') || {};
  if (!Array.isArray(tables.outages) || tables.outages.length === 0) return;

  // Build a map of cellId -> outages for efficient lookup
  const outagesByCellId = new Map();
  for (const outage of tables.outages) {
    if (outage.cellId) {
      if (!outagesByCellId.has(outage.cellId)) {
        outagesByCellId.set(outage.cellId, []);
      }
      // Store outage without cellId since it's stored on the cell itself
      const { cellId, ...outageData } = outage;
      outagesByCellId.get(outage.cellId).push(outageData);
    }
  }

  // Apply outages to cells
  graph.getCells().forEach((cell) => {
    if (outagesByCellId.has(cell.id)) {
      cell.set('outages', outagesByCellId.get(cell.id), { silent: true });
    }
  });
};

const getDeep = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('/').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : undefined), obj);
};

const getCellDisplayName = (cell) => {
  if (!cell) return null;
  const v =
    cell.get('cellName') ??
    cell.prop('attrs/label/text') ??
    (typeof cell.attr === 'function' ? cell.attr(['label', 'text']) : null);
  if (typeof v === 'string' && v.trim().length) return v.trim();
  return null;
};

const JointJSMap = ({ height, initialGraph, onGraphChange, readOnly }) => {
  const rappid = useRef(null);
  const modulesRef = useRef([]);
  const elementRef = useRef(null);
  const paperContainer = useRef(null);
  const stencilContainer = useRef(null);
  const inspectorContainer = useRef(null);
  const navigatorContainer = useRef(null);
  const openGroupsButton = useRef(null);
  const closeGroupsButton = useRef(null);
  const inspectorHeader = useRef(null);
  const inspectorContent = useRef(null);
  const inspectorBody = useRef(null);
  const servicesRef = useRef(null);
  const originalInitialJsonRef = useRef(null);
  const lastSavedGraphRef = useRef(null); // Track last saved state for discard
  const freeTransformRef = useRef(null);
  const [modules, setModules] = useState([]);
  const [stockCellNames, setStockCellNames] = useState([]);
  const [searchCells, setSearchCells] = useState([]);
  const [searchTransports, setSearchTransports] = useState([]);
  const [configErrors, setConfigErrors] = useState([]);
  const [isErrorsDialogOpen, setIsErrorsDialogOpen] = useState(false);
  const highlightedModules = useRef([]);
  const [selectedCell, setSelectedCell] = useState(null);

  // Tooltip state
  const [hoveredCell, setHoveredCell] = useState(null);
  const hoveredAnchorEl = useRef(null);
  const inspectorOpen = Boolean(selectedCell);
  const [activeTab, setActiveTab] = useState(null);
  const [tables, setTables] = useState(null);

  const isDarkThemeUsed = useIsDarkTheme();
  const theme = useMemo(() => getTheme(isDarkThemeUsed), [isDarkThemeUsed]);
  const currentThemeName = useMemo(() => (isDarkThemeUsed ? 'csmdark' : 'light'), [isDarkThemeUsed]);

  const handleTabClick = useCallback((targetTab) => {
    setActiveTab((prev) => (prev === targetTab ? null : targetTab));
  }, []);

  const handleTableSave = useCallback((dataKey, rows) => {
    const key = typeof dataKey === 'string' ? dataKey : (dataKeyMap[dataKey]?.key ?? dataKey);
    const graph = rappid.current.graph;
    const currentGraphTables = graph.get('tables') || {};
    const normalizedRows =
      key === 'outages'
        ? rows.map((r) => ({
            ...r,
            startTime: convertDateTimetoTime(r.startTime),
            endTime: convertDateTimetoTime(r.endTime),
          }))
        : rows;
    const updated = { ...currentGraphTables, [key]: normalizedRows };
    graph.set('tables', updated);
    setTables(updated);
    refreshStockNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGraphChange = useCallback(() => {
    if (onGraphChange && !readOnly) {
      onGraphChange(rappid.current.graph);
    }
  }, [onGraphChange, readOnly]);

  const handleZoomToFitFullMap = useCallback(() => {
    if (rappid.current) {
      rappid.current.zoomToFitFullMap();
    }
  }, []);

  const handleZoomOnHighlightedElements = useCallback(() => {
    if (rappid.current) {
      rappid.current.zoomOnHighlightedElements();
    }
  }, []);

  const computeConfigErrors = useCallback(() => {
    if (!rappid.current || !rappid.current.graph) return [];
    const graph = rappid.current.graph;
    const errors = [];

    // Helper: get the raw value stored on the cell for a given field
    const getRawValueForField = (cell, field, fieldKey) => {
      if (!field || !field.path) return undefined;

      const isEmptyLike = (val) =>
        val === undefined || val === null || val === '' || (typeof val === 'string' && val.trim() === '');

      // 1) main path from inspector (e.g. "customAttributes/capacityPerHour")
      let value = getDeep(cell.attributes, field.path);

      // 2) if it’s a customAttributes path and empty, fall back to the root key
      if (isEmptyLike(value) && field.path.startsWith('customAttributes/')) {
        const lastKey = field.path.split('/').pop(); // "capacityPerHour", "duration", …
        if (lastKey) {
          const rootValue = cell.get(lastKey);
          if (!isEmptyLike(rootValue)) {
            value = rootValue;
          }
        }
      }

      // 3) as an extra safety, try the fieldKey itself if still empty
      if (isEmptyLike(value) && fieldKey) {
        const keyValue = cell.get(fieldKey);
        if (!isEmptyLike(keyValue)) {
          value = keyValue;
        }
      }

      return value;
    };

    graph.getCells().forEach((cell) => {
      const type = cell.get('type');
      const def = inspectorDefinitions[type];
      const inputs = def && def.inputs;
      if (!inputs) return;

      const isLink = typeof cell.isLink === 'function' && cell.isLink();

      // Display name we already show in the table (DIV1, PFT1, …)
      const displayName = getCellDisplayName(cell) ?? '';

      const transportName = (() => {
        if (!isLink) return '';
        const source = cell.get('source') || {};
        const target = cell.get('target') || {};
        const srcCell = source.id ? graph.getCell(source.id) : null;
        const tgtCell = target.id ? graph.getCell(target.id) : null;
        const srcName = getCellDisplayName(srcCell) || 'Unknown';
        const tgtName = getCellDisplayName(tgtCell) || 'Unknown';
        return `${srcName}_${tgtName}`;
      })();

      // For links, we need the current useSpeed value to know if distance/speed are required
      let useSpeedValue = false;
      if (isLink && inputs.useSpeed) {
        const rawUseSpeed = getRawValueForField(cell, inputs.useSpeed, 'useSpeed');
        useSpeedValue = rawUseSpeed === true || rawUseSpeed === 'true';
      }

      Object.keys(inputs).forEach((fieldKey) => {
        const field = inputs[fieldKey];
        if (!field || !field.path) return;

        // Base “required” rule: only required fields are candidates
        let isRequired = !!field.required;

        // Special rule for transports: distanceMeters + speed only required when useSpeed is true
        if (isLink && (field.path === 'customAttributes/distanceMeters' || field.path === 'customAttributes/speed')) {
          isRequired = isRequired && useSpeedValue;
        }

        // If not required according to the business rules, skip
        if (!isRequired) return;

        if (field.readOnly && field.path !== 'customAttributes/distanceMeters') {
          return;
        }

        // eslint-disable-next-line prefer-const
        let value = getRawValueForField(cell, field, fieldKey);

        if (field.path === 'customAttributes/cellName') {
          const inspectorEmpty =
            value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

          if (!inspectorEmpty) {
            return;
          }
          if (displayName) {
            return;
          }
        }

        let isEmpty = false;

        if (
          isLink &&
          useSpeedValue &&
          (field.path === 'customAttributes/distanceMeters' || field.path === 'customAttributes/speed')
        ) {
          isEmpty =
            value === undefined ||
            value === null ||
            value === '' ||
            (typeof value === 'number' && value === 0) ||
            (typeof value === 'string' && (value.trim() === '' || value.trim() === '0'));
        }
        // For capacityPerHour on subfunctions: treat 0 as “not filled”
        else if (field.path === 'customAttributes/capacityPerHour') {
          isEmpty =
            value === undefined ||
            value === null ||
            value === '' ||
            value === 0 ||
            (typeof value === 'string' && (value.trim() === '' || value.trim() === '0'));
        }
        // Generic required rule for everything else
        else {
          isEmpty =
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim().length === 0) ||
            (Array.isArray(value) && value.length === 0);
        }

        if (!isEmpty) return;

        const typeLabel = isLink ? 'Transport' : 'Sous-fonction';
        const name = isLink ? transportName : displayName;
        const fullName = cell.get('customAttributes')?.fullName;
        const context = fullName || '';
        const label = field.label || fieldKey;
        const message = `Paramètre obligatoire "${label}" manquant`;

        errors.push({
          id: `${cell.id}-${fieldKey}`,
          cellId: cell.id,
          fieldKey,
          typeLabel,
          name,
          context,
          message,
          __kind: isLink ? 'transport' : 'cell',
        });
      });
    });

    // Check for duplicate subfunction names
    const subfunctionCells = graph.getCells().filter((cell) => {
      const isLink = typeof cell.isLink === 'function' && cell.isLink();
      if (isLink) return false;
      return getCellDisplayName(cell) !== null;
    });

    const nameToIds = {};
    subfunctionCells.forEach((cell) => {
      const name = getCellDisplayName(cell);
      if (!nameToIds[name]) nameToIds[name] = [];
      nameToIds[name].push(cell);
    });

    Object.entries(nameToIds).forEach(([name, cells]) => {
      if (cells.length > 1) {
        cells.forEach((cell) => {
          const fullName = cell.get('customAttributes')?.fullName;
          errors.push({
            id: `${cell.id}-duplicate-name`,
            cellId: cell.id,
            fieldKey: 'cellName',
            typeLabel: 'Sous-fonction',
            name,
            context: fullName || '',
            message: "Nom de sous-fonction dupliqué, renommez l'une des sous-fonctions",
            __kind: 'cell',
          });
        });
      }
    });

    return errors;
  }, []);

  const handleOpenErrorsDialog = useCallback(() => {
    const errors = computeConfigErrors();
    setConfigErrors(errors);
    setIsErrorsDialogOpen(true);
  }, [computeConfigErrors]);

  const onModuleCreated = useCallback(
    (newModule) => {
      modulesRef.current = [...modulesRef.current, newModule];
      setModules([...modulesRef.current]);
      rappid.current.setModules(modulesRef.current);
      rappid.current?.graph?.set('modules', modulesRef.current);
      servicesRef.current.inspectorService.setModules(modulesRef.current);
      servicesRef.current.inspectorService.inspector?.render();
      handleGraphChange();
    },
    [handleGraphChange]
  );

  const onModuleDeleted = useCallback(
    (deletedModule) => {
      modulesRef.current = modulesRef.current.filter((m) => m !== deletedModule);
      setModules([...modulesRef.current]);
      rappid.current.setModules(modulesRef.current);
      rappid.current?.graph?.set('modules', modulesRef.current);
      servicesRef.current.inspectorService.setModules(modulesRef.current);
      servicesRef.current.inspectorService.inspector?.render();
      deleteModuleElements(rappid.current.graph, deletedModule);
      handleGraphChange();
    },
    [handleGraphChange]
  );
  const onGraphDownload = useCallback(() => {
    rappid.current.graph.set('modules', modulesRef.current);
    exportGraphJsonFile(rappid.current.graph);
  }, []);

  const computeAllStockNames = useCallback(() => {
    const graph = rappid.current?.graph;
    if (!graph) return [];

    const isStockCell = (cell) => {
      const t = String(cell.get('type') ?? cell.get('name') ?? cell.prop('type') ?? '');
      return /(^|[./])stock$/i.test(t) || /stock/i.test(t);
    };

    const fromCells = graph
      .getCells()
      .filter(isStockCell)
      .map(getCellDisplayName)
      .filter((v) => typeof v === 'string');

    const fromTable = (graph.get('tables')?.stockChoiceTable || [])
      .map((r) => (typeof r?.stockName === 'string' ? r.stockName.trim() : null))
      .filter((v) => v);

    return Array.from(new Set([...fromCells, ...fromTable])).sort((a, b) => a.localeCompare(b));
  }, []);

  const computeSearchableCells = useCallback(() => {
    const graph = rappid.current?.graph;
    if (!graph) return [];

    return graph
      .getCells()
      .filter((cell) => !(typeof cell.isLink === 'function' && cell.isLink()))
      .map((cell) => {
        const id = cell.id;
        const name = getCellDisplayName(cell);
        if (!name) return null;
        return { id, name };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const computeSearchableTransports = useCallback(() => {
    const graph = rappid.current?.graph;
    if (!graph) return [];

    return graph
      .getLinks()
      .map((link) => {
        const id = link.id;
        const source = link.get('source') || {};
        const target = link.get('target') || {};

        const srcCell = source.id ? graph.getCell(source.id) : null;
        const tgtCell = target.id ? graph.getCell(target.id) : null;

        const sourceName = getCellDisplayName(srcCell);
        const destinationName = getCellDisplayName(tgtCell);

        if (!sourceName && !destinationName) return null;

        const name = sourceName && destinationName ? `${sourceName}_${destinationName}` : sourceName || destinationName;

        return {
          id,
          name,
          sourceName: sourceName || null,
          destinationName: destinationName || null,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const refreshStockNames = useCallback(() => {
    setStockCellNames(computeAllStockNames());
  }, [computeAllStockNames]);

  const refreshSearchCells = useCallback(() => {
    setSearchCells(computeSearchableCells());
  }, [computeSearchableCells]);

  const refreshSearchTransports = useCallback(() => {
    setSearchTransports(computeSearchableTransports());
  }, [computeSearchableTransports]);

  const onGraphUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const text = await file.text();
      const json = JSON.parse(text);
      if (json.tables && Array.isArray(json.tables)) {
        json.tables = {};
      }
      const { cells, tables, modules } = importGraphJsonFile(json);
      const graph = rappid.current.graph;

      const prevZoom = rappid.current.paperScroller?.zoom();
      const prevCenter = rappid.current.paperScroller?.getCenter();

      graph.resetCells(cells, { silent: true });
      graph.fromJSON({ cells });
      graph.set('tables', tables);
      if (Array.isArray(modules)) {
        graph.set('modules', modules);
      }
      // Sync outages from tables to cells after graph load
      syncCellOutagesFromTables(graph);

      if (rappid.current.paperScroller && prevZoom && prevCenter) {
        setTimeout(() => {
          rappid.current.paperScroller.zoom(prevZoom, { absolute: true });
          rappid.current.paperScroller.center(prevCenter.x, prevCenter.y);
        }, 0);
      }

      const hasModulesKey = Object.prototype.hasOwnProperty.call(json, 'modules');
      const newModules = hasModulesKey ? (Array.isArray(modules) ? modules : []) : getModulesFromGraph(graph);

      setTables(tables);
      refreshStockNames();
      refreshSearchCells();
      refreshSearchTransports();
      graph.set('modules', newModules);

      modulesRef.current = newModules;
      setModules(newModules);
      rappid.current.setModules(newModules);
      handleGraphChange();
    };
    input.click();
  }, [handleGraphChange, refreshStockNames, refreshSearchCells, refreshSearchTransports]);

  useEffect(() => {
    if (!readOnly && rappid.current?.graph) {
      const graph = rappid.current.graph;
      const handleRename = (cell) => {
        const prev = cell.previous('attrs');
        const oldName = prev?.label?.text;
        const newName = cell.prop('attrs/label/text');
        if (oldName && oldName !== newName) {
          const tables = graph.get('tables') || {};
          const rows = tables.stockChoiceTable || [];
          const updatedRows = rows.map((r) => (r.stockName === oldName ? { ...r, stockName: newName } : r));
          if (!rows.every((r, i) => r.stockName === updatedRows[i]?.stockName)) {
            const updatedTables = { ...tables, stockChoiceTable: updatedRows };
            graph.set('tables', updatedTables);
            setTables(updatedTables);
            refreshStockNames();
          }
        }
        refreshSearchCells();
        refreshSearchTransports();
        handleGraphChange();
      };
      graph.on('change:attrs', handleRename);
      return () => graph.off('change:attrs', handleRename);
    }
  }, [readOnly, handleGraphChange, refreshStockNames, refreshSearchCells, refreshSearchTransports]);

  useEffect(() => {
    servicesRef.current = {
      stencilService: new StencilService(stencilContainer.current, isDarkThemeUsed),
      inspectorService: new InspectorService({
        openGroupsButton: openGroupsButton.current,
        closeGroupsButton: closeGroupsButton.current,
        container: inspectorBody.current,
        header: inspectorHeader.current,
        content: inspectorContent.current,
        isDarkThemeUsed,
        closeInspector: () => setSelectedCell(null),
        onInspectorChange: handleGraphChange,
        readOnly,
      }),
      haloService: new HaloService(),
      keyboardService: new KeyboardService(),
      navigatorService: new NavigatorService(navigatorContainer.current, theme),
    };
    rappid.current = new RappidService(elementRef.current, paperContainer.current, servicesRef.current, {
      theme: currentThemeName,
      modules: modulesRef.current,
      readOnly,
    });
    rappid.current.startRappid();

    const graph = rappid.current.graph;
    const rawData =
      initialGraph && Object.keys(initialGraph).length
        ? initialGraph
        : sampleGraphs.emergencyProcedure
          ? JSON.parse(sampleGraphs.emergencyProcedure)
          : null;
    if (rawData) {
      const jsonData = cleanJson(rawData);
      try {
        graph.fromJSON(jsonData, { ignoreUndoRedo: true });
      } catch (err) {
        console.warn('Failed to load graph JSON cleanly', err);
      }
      // Sync outages from tables to cells after graph load
      syncCellOutagesFromTables(graph);
      try {
        originalInitialJsonRef.current = JSON.parse(JSON.stringify(jsonData));
      } catch {}
    }

    const providedModules = Array.isArray(rawData?.modules) ? rawData.modules.map(sanitizeModuleName) : null;
    const graphAttrRaw = graph.get('modules');
    const graphAttrModules = Array.isArray(graphAttrRaw) ? graphAttrRaw.map(sanitizeModuleName) : null;

    const initialModules = providedModules ?? graphAttrModules ?? getModulesFromGraph(graph);

    graph.set('modules', initialModules);
    modulesRef.current = initialModules;
    setModules(initialModules);
    rappid.current.setModules(initialModules);
    setTables(graph.get('tables') || {});
    refreshStockNames();
    refreshSearchCells();
    refreshSearchTransports();

    const handleGraphStructureChanged = () => {
      refreshStockNames();
      refreshSearchCells();
      refreshSearchTransports();
    };
    graph.on('change add remove change:attrs', handleGraphStructureChanged);

    const paper = rappid.current.paper;
    const clearSelection = () => {
      servicesRef.current.inspectorService.removeIfOpen?.();
      setSelectedCell(null);
    };
    const handleCellClick = (cellView) => {
      if (servicesRef.current.inspectorService.isSelectingDestination) return;
      const cell = cellView.model;
      setSelectedCell(cell);
      const inspector = servicesRef.current.inspectorService.create(cell, graph, paper, modulesRef.current);
      if (!readOnly && inspector) inspector.on('update', debouncedGraphChange);
    };
    paper.on('blank:pointerdown', clearSelection);
    paper.on('cell:pointerclick', handleCellClick);

    // Tooltip handlers: show tooltip with ONLY cellName; never for links
    const handleMouseEnter = (cellView) => {
      const model = cellView?.model;
      if (!model) return;
      if (typeof model.isLink === 'function' && model.isLink()) {
        setHoveredCell(null);
        hoveredAnchorEl.current = null;
        return;
      }
      hoveredAnchorEl.current = cellView.el || null;
      setHoveredCell(model);
    };
    const handleMouseLeave = () => {
      hoveredAnchorEl.current = null;
      setHoveredCell(null);
    };
    const handleBlankLeave = () => {
      hoveredAnchorEl.current = null;
      setHoveredCell(null);
    };

    paper.on('cell:mouseenter', handleMouseEnter);
    paper.on('cell:mouseleave', handleMouseLeave);
    paper.on('blank:mouseleave', handleBlankLeave);

    let changeTimeout;
    const debouncedGraphChange = () => {
      clearTimeout(changeTimeout);
      changeTimeout = setTimeout(() => {
        handleGraphChange();

        if (isErrorsDialogOpen) {
          setConfigErrors(computeConfigErrors());
        }
      }, 150);
    };

    if (!readOnly) {
      graph.on('change add remove', debouncedGraphChange);
      graph.on('change:attrs', debouncedGraphChange);
      graph.on('change:tables', debouncedGraphChange);
    }
    const handleCellRemoval = (cell) => {
      if (!cell) return;

      // Clean up outages associated with the removed cell
      try {
        const tables = graph.get('tables') || {};
        if (Array.isArray(tables.outages) && tables.outages.length > 0) {
          const cellId = cell.id;
          const filteredOutages = tables.outages.filter((o) => o.cellId !== cellId);
          if (filteredOutages.length !== tables.outages.length) {
            tables.outages = filteredOutages;
            graph.set('tables', tables);
          }
        }
      } catch (e) {
        console.warn('[cleanup] Failed to remove outages for deleted cell', e);
      }

      // Link-specific cleanup
      if (!cell.isLink || !cell.isLink()) return;

      const source = cell.get('source') || {};
      const target = cell.get('target') || {};

      const srcCell = graph.getCell(source.id);
      const tgtCell = graph.getCell(target.id);

      // Helper: clear a port field if that port exists
      const safePortProp = (node, portId, path, value) => {
        if (!node || !portId || !node.getPort || !node.getPort(portId)) return;
        node.portProp(portId, path, value);
        // Ensure React inspector sees the change
        node.trigger('change:ports', node, node.get('ports'));
      };

      try {
        // If the link originated from a TRI port, that port’s "destination" is now invalid
        if (srcCell && source.port) {
          // Clear both the raw field and the commonly used alias
          safePortProp(srcCell, source.port, 'destination', '');
          safePortProp(srcCell, source.port, 'data/destination', '');
        }
        // If the link ended at a TRI port, that port’s "source" is now invalid
        if (tgtCell && target.port) {
          safePortProp(tgtCell, target.port, 'source', null);
          safePortProp(tgtCell, target.port, 'data/source', null);
        }
      } catch (e) {
        console.warn('[cleanup] Failed to clear port metadata after link removal', e);
      }

      // Link removal also changes transports
      refreshSearchTransports();
    };
    graph.on('remove', handleCellRemoval);
    return () => {
      paper.off('blank:pointerdown', clearSelection);
      paper.off('cell:pointerclick', handleCellClick);

      paper.off('cell:mouseenter', handleMouseEnter);
      paper.off('cell:mouseleave', handleMouseLeave);
      paper.off('blank:mouseleave', handleBlankLeave);

      if (!readOnly) {
        graph.off('change add remove', debouncedGraphChange);
        graph.off('change:attrs', debouncedGraphChange);
        graph.off('change:tables', debouncedGraphChange);
      }
      graph.off('remove', handleCellRemoval);
      graph.off('change add remove change:attrs', handleGraphStructureChanged);
      if (freeTransformRef.current) {
        freeTransformRef.current.remove();
        freeTransformRef.current = null;
      }
      rappid.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentThemeName,
    isDarkThemeUsed,
    readOnly,
    theme,
    handleGraphChange,
    refreshStockNames,
    refreshSearchCells,
    refreshSearchTransports,
    computeConfigErrors,
  ]);

  useEffect(() => {
    const onDiscard = () => {
      if (!rappid.current) {
        return;
      }

      const rawDataToRestore = lastSavedGraphRef.current || originalInitialJsonRef.current;
      if (!rawDataToRestore) {
        console.error('❌ No graph data to restore');
        return;
      }

      const graph = rappid.current.graph;
      const paper = rappid.current.paper;

      graph.clear();

      const jsonData = cleanJson(rawDataToRestore);
      try {
        graph.fromJSON(jsonData, { ignoreUndoRedo: true });
      } catch (err) {
        console.error('Failed to restore graph JSON', err);
        return;
      }
      // Sync outages from tables to cells after graph restore
      syncCellOutagesFromTables(graph);

      const providedModules = Array.isArray(rawDataToRestore?.modules)
        ? rawDataToRestore.modules.map(sanitizeModuleName)
        : null;
      const graphAttrRaw = graph.get('modules');
      const graphAttrModules = Array.isArray(graphAttrRaw) ? graphAttrRaw.map(sanitizeModuleName) : null;
      const restoredModules = providedModules ?? graphAttrModules ?? getModulesFromGraph(graph);

      graph.set('modules', restoredModules);
      modulesRef.current = restoredModules;
      setModules(restoredModules);
      rappid.current.setModules(restoredModules);

      setTables(graph.get('tables') || {});

      servicesRef.current?.inspectorService?.removeIfOpen?.();
      setSelectedCell(null);

      refreshStockNames();
      refreshSearchCells();
      refreshSearchTransports();

      if (paper) {
        paper.unfreeze();
        setTimeout(() => {
          if (rappid.current?.paperScroller) {
            rappid.current.paperScroller.centerContent();
          }
        }, 100);
      }
    };
    window.addEventListener('interactiveMap:discard', onDiscard);
    return () => window.removeEventListener('interactiveMap:discard', onDiscard);
  }, [refreshStockNames, refreshSearchCells, refreshSearchTransports]);

  useEffect(() => {
    const onSave = () => {
      if (!rappid.current) return;

      const graph = rappid.current.graph;
      const currentState = {
        cells: graph.toJSON().cells,
        modules: [...modulesRef.current],
        tables: { ...(graph.get('tables') || {}) },
      };
      lastSavedGraphRef.current = currentState;
    };

    window.addEventListener('interactiveMap:save', onSave);

    return () => {
      window.removeEventListener('interactiveMap:save', onSave);
    };
  }, []);

  // Compute tooltip content & styling for hovered cell (ONLY use cellName; no fallbacks)
  const cellName = useMemo(() => {
    if (!hoveredCell) return null;
    const n = typeof hoveredCell.get === 'function' ? hoveredCell.get('cellName') : null;
    if (typeof n === 'string' && n.trim().length) return n.trim();
    return null;
  }, [hoveredCell]);
  const isMissingName = !cellName;
  const tooltipTitle = isMissingName ? 'Nom Manquant - Champ Requis' : cellName;

  // Do not render tooltip at all for links
  const shouldShowTooltip = !!hoveredCell && !(typeof hoveredCell.isLink === 'function' && hoveredCell.isLink());

  const handleInspectorClose = useCallback(() => {
    servicesRef.current?.inspectorService?.removeIfOpen?.();
    setSelectedCell(null);
  }, []);
  useEffect(() => {
    const onClose = () => handleInspectorClose();
    window.addEventListener('interactiveMap:closeInspector', onClose);
    return () => window.removeEventListener('interactiveMap:closeInspector', onClose);
  }, [handleInspectorClose]);
  const showSearchSelection = useCallback((cellView) => {
    if (!cellView) return;

    const model = cellView.model;
    if (typeof model.isLink === 'function' && model.isLink()) {
      return;
    }

    if (freeTransformRef.current) {
      freeTransformRef.current.remove();
      freeTransformRef.current = null;
    }

    freeTransformRef.current = new joint.ui.FreeTransform({
      cellView,
      allowRotation: false,
      preserveAspectRatio: false,
    });

    freeTransformRef.current.render();
  }, []);

  const handleCellSearchSelect = useCallback(
    (option) => {
      if (!option || !rappid.current) return;

      const graph = rappid.current.graph;
      const paper = rappid.current.paper;
      const paperScroller = rappid.current.paperScroller;
      const selection = rappid.current.selection;
      if (!graph || !paper || !selection) return;

      let cell = null;

      if (option.id) {
        cell = graph.getCell(option.id);
      }
      if (!cell && option.name) {
        const targetName = option.name;
        cell = graph.getCells().find((c) => getCellDisplayName(c) === targetName);
      }

      if (!cell) return;

      selection.collection.reset([cell]);

      const cellView = paper.findViewByModel(cell);
      if (cellView) {
        showSearchSelection(cellView);
      }
      setSelectedCell(cell);
      const inspector = servicesRef.current.inspectorService.create(cell, graph, paper, modulesRef.current);
      if (!readOnly && inspector) {
        inspector.on('update', () => {
          handleGraphChange();
        });
      }

      // Zoom and center on the cell
      if (paperScroller && typeof cell.getBBox === 'function') {
        setTimeout(() => {
          const bbox = cell.getBBox();
          const cx = bbox.x + bbox.width / 2;
          const cy = bbox.y + bbox.height / 2;

          paperScroller.zoom(1.5, { absolute: true });
          setTimeout(() => {
            paperScroller.center(cx, cy);
          }, 50);
        }, 100);
      }
    },
    [handleGraphChange, readOnly, showSearchSelection]
  );

  const handleTransportSearchSelect = useCallback(
    (option) => {
      if (!option || !rappid.current) return;

      const graph = rappid.current.graph;
      const paper = rappid.current.paper;
      const paperScroller = rappid.current.paperScroller;
      const selection = rappid.current.selection;
      if (!graph || !paper || !selection) return;

      let link = null;

      if (option.id) {
        link = graph.getCell(option.id);
      }

      if (!link || !link.isLink || !link.isLink()) return;

      const linkView = paper.findViewByModel(link);
      if (!linkView) return;
      selection.collection.reset([link]);

      setSelectedCell(link);
      const inspector = servicesRef.current.inspectorService.create(link, graph, paper, modulesRef.current);
      if (!readOnly && inspector) {
        inspector.on('update', handleGraphChange);
      }

      if (paperScroller && typeof link.getBBox === 'function') {
        setTimeout(() => {
          const bbox = link.getBBox();
          const cx = bbox.x + bbox.width / 2;
          const cy = bbox.y + bbox.height / 2;

          paperScroller.zoom(1.5, { absolute: true });
          setTimeout(() => {
            paperScroller.center(cx, cy);
          }, 50);
        }, 100);
      }
    },
    [handleGraphChange, readOnly]
  );

  const handleErrorRowClick = useCallback(
    (err) => {
      if (!err || !rappid.current) return;

      const option = {
        id: err.cellId,
        name: err.name,
        __kind: err.__kind,
      };

      if (err.__kind === 'transport') {
        handleTransportSearchSelect(option);
      } else {
        handleCellSearchSelect(option);
      }

      setIsErrorsDialogOpen(false);
    },
    [handleCellSearchSelect, handleTransportSearchSelect]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Box
          id="jointjs-map-container"
          sx={{ width: '100%', height: height ?? '100%', overflow: 'hidden', position: 'relative' }}
        >
          <Grid container direction="column" wrap="nowrap" sx={{ height: '100%' }}>
            <Grid item>
              <Paper
                elevation={4}
                className="toolbar-container"
                data-cy="toolbar-container"
                sx={{ borderBottom: `1px solid ${theme.palette.divider}`, minHeight: theme.spacing(6) }}
              >
                <Toolbar
                  modules={modules}
                  onModuleCreated={onModuleCreated}
                  onModuleSelectionChange={(m) => {
                    highlightedModules.current = m.length ? m : modulesRef.current;
                    rappid.current.setHighlightedModules(highlightedModules.current);
                    updateCellOpacity(rappid.current.graph, highlightedModules.current);
                  }}
                  onModuleDeleted={onModuleDeleted}
                  onGraphDownload={onGraphDownload}
                  onGraphUpload={onGraphUpload}
                  tabTitles={dataKeyMap}
                  activeTab={activeTab}
                  onTabChange={handleTabClick}
                  onZoomToFitFullMap={handleZoomToFitFullMap}
                  onZoomOnHighlightedElements={handleZoomOnHighlightedElements}
                  readOnly={readOnly}
                  cells={searchCells}
                  transports={searchTransports}
                  onCellSearchSelect={handleCellSearchSelect}
                  onTransportSearchSelect={handleTransportSearchSelect}
                  onOpenErrorsDialog={handleOpenErrorsDialog}
                />
              </Paper>
            </Grid>
            <Grid id="map-and-panels-container" item container sx={{ flex: 1, overflow: 'hidden' }}>
              <Grid
                item
                ref={stencilContainer}
                className="stencil-container"
                data-cy="stencil-container"
                sx={{
                  width: '120px',
                  borderRight: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.paper,
                  position: 'relative',
                }}
              />
              <Grid
                item
                xs
                ref={paperContainer}
                className="paper-container"
                data-cy="paper-container"
                sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}
              />
              <Grid
                item
                xs="auto"
                ref={inspectorContainer}
                className="inspector-container"
                data-cy="inspector-container"
                sx={{
                  height: 'calc(100% - 40px) !important',
                  width: inspectorOpen ? '520px' : '0px',
                  minWidth: inspectorOpen ? '520px' : '0px',
                  maxWidth: inspectorOpen ? '520px' : '0px',
                  flexBasis: inspectorOpen ? '520px' : '0px',
                  transition: 'all 0.3s ease-in-out',
                  overflow: 'visible',
                  position: 'relative',
                  zIndex: 1200,
                  backgroundColor: theme.palette.background.paper,
                  display: inspectorOpen ? 'flex' : 'none',
                  flexDirection: 'column',
                }}
              >
                <Box ref={inspectorHeader} className="inspector-header hidden" sx={{ display: 'none' }} />

                <Box
                  ref={inspectorContent}
                  className="inspector-content"
                  data-cy="inspector-content"
                  sx={{
                    overflowY: 'auto',
                    overflowX: 'visible',
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {inspectorOpen && selectedCell && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        p: 0.1,
                        mb: 1,
                        mr: 0,
                        width: 'fit-content',
                        position: 'absolute !important',
                        top: 0,
                        right: 0,
                        zIndex: 1300,
                      }}
                    >
                      <Tooltip title="Fermer le menu" sx={{ width: 'fit-content' }}>
                        <CloseOutlined
                          fontSize="medium"
                          aria-label="Close inspector"
                          onClick={handleInspectorClose}
                          data-cy="inspector-close-btn"
                          sx={{
                            width: 'fit-content',
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: '100%',
                            p: 0.25,
                          }}
                        />
                      </Tooltip>
                    </Box>
                  )}

                  <Box
                    ref={inspectorBody}
                    data-cy="inspector-body"
                    sx={{ flex: 1, position: 'relative', px: 1, pb: 2 }}
                  />
                </Box>
              </Grid>

              <Grid
                item
                xs="auto"
                ref={navigatorContainer}
                className="navigator-container"
                data-cy="navigator-container"
                sx={{
                  width: '60px',
                  borderLeft: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              />
            </Grid>
          </Grid>
          <div ref={elementRef} style={{ display: 'none' }} />

          {shouldShowTooltip && (
            <Tooltip
              open
              arrow
              title={tooltipTitle}
              placement="top"
              enterDelay={150}
              enterNextDelay={0}
              disableInteractive
              componentsProps={{
                tooltip: {
                  sx: isMissingName
                    ? {
                        bgcolor: (t) => alpha(t.palette.error.main, 0.95),
                        color: (t) => t.palette.common.white,
                        fontWeight: 600,
                        '& .MuiTooltip-arrow': {
                          color: (t) => alpha(t.palette.error.main, 0.95),
                        },
                      }
                    : {},
                },
                popper: {
                  disablePortal: true,
                },
              }}
              PopperProps={{
                anchorEl: hoveredAnchorEl.current,
                modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
              }}
            >
              <span />
            </Tooltip>
          )}

          <ErrorListDialog
            open={isErrorsDialogOpen}
            onClose={() => setIsErrorsDialogOpen(false)}
            errors={configErrors}
            onRowClick={handleErrorRowClick}
          />

          <TabDataDialog
            open={Boolean(activeTab)}
            onClose={() => setActiveTab(null)}
            activeTab={activeTab || undefined}
            onSave={readOnly ? null : handleTableSave}
            initialData={tables || {}}
            dynamicOptions={{
              name: modules,
              currentModule: modules,
              exitModule: modules,
              stockName: stockCellNames,
            }}
            readOnly={readOnly}
          />
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

JointJSMap.propTypes = {
  height: PropTypes.any,
  initialGraph: PropTypes.object,
  onGraphChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default React.memo(JointJSMap);
